import styled from "styled-components/macro"
import { useStore } from "@state/store"
import { useEffect, useState } from "react"
import gsap from "gsap"

import FloorPlate from "../FloorPlate"

import { poundFormatter } from "../table/TableRow"
import {
  flattenedMap,
  FLOORPLAN_MEASURES,
  FLOORPLAN_NAME,
} from "@data/planTypesData"
import SphericalTour from "../SphericalTour"
import Specs from "../Specs"
import TextButton from "@common/ui/TextButton"
import Popup from "../Popup"

const FloorplanImage = ({ unit, isFav = true }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedUnit, setSelectedUnit] = useState(null)
  const contentControls = useStore((s) => s.contentControls)
  const planID = unit ? flattenedMap[unit[0]?.planType] : null

  const handleModalOpen = (open: boolean, selectedUnit: any) => {
    setIsModalOpen(open)
    setSelectedUnit(selectedUnit)
  }

  useEffect(() => {
    gsap.to("#floorplan", {
      autoAlpha: contentControls?.index === 0 ? 1 : 0,
      duration: 0.6,
      ease: "power3.inOut",
    })
    gsap.to("#spec", {
      autoAlpha: contentControls?.index === 1 ? 1 : 0,
      duration: 0.6,
      ease: "power3.inOut",
    })
    gsap.to("#view", {
      autoAlpha: contentControls?.index === 2 ? 1 : 0,
      duration: 0.6,
      ease: "power3.inOut",
    })
  }, [contentControls])

  return (
    planID && (
      <>
        <Wrapper>
          <Header>
            <h1>
              {FLOORPLAN_NAME[planID]}
              <br />
              <span>apartment {unit[0]?.planType}</span>
            </h1>
            <h1
              style={{
                transformOrigin: "center bottom",
                transform: "scale(0.7)",
                textAlign: "right",
              }}
            >
              FLOOR {unit[0]?.floor}
              <br />
              <span>
                {Number.isNaN(unit[0]?.price)
                  ? "unreleased"
                  : poundFormatter.format(unit[0]?.price)}
              </span>
            </h1>
          </Header>
          <FloorplanWrapper>
            <div id="floorplan">
              <img
                src={`/assets/imgs/plans/TYPE-${planID}.svg`}
                alt={unit[0]?.floorplan}
                className="plan"
              />
              <img
                src={`/assets/icons/compass.svg`}
                alt={unit[0]?.floorplan}
                className="compass"
              />
            </div>
            <div id="view">
              <SphericalTour
                unit={unit}
                texture={"/assets/imgs/view.webp"}
                initialPosition={[0, 0, 0]}
              />
            </div>
            <div id="spec">
              <Specs />
            </div>
            {!isFav && contentControls?.index !== 1 && (
              <TextButton
                className="imageOverlayToggleFav"
                onClick={() => handleModalOpen(true, unit)}
                variant="fill"
                color="primary"
                icon={
                  <img
                    src="assets/icons/enlarge.png"
                    alt=""
                    style={{ filter: "invert(100%)" }}
                  />
                }
              />
            )}
          </FloorplanWrapper>
          <Footer>
            <Measures>
              {Object.keys(FLOORPLAN_MEASURES[planID])?.map((key, i) => {
                return (
                  <div key={`planID-${unit[0].unitName}-${i}`}>
                    <h3>{key}</h3>
                    <h3>
                      {FLOORPLAN_MEASURES[planID][key].map((txt, i) => {
                        return <span key={`${txt}-${i}`}>{txt}</span>
                      })}
                    </h3>
                  </div>
                )
              })}
            </Measures>
            <FloorPlate planType={planID} />
          </Footer>
        </Wrapper>
        <Popup
          openState={isModalOpen}
          setOpenState={setIsModalOpen}
          onClose={() => setIsModalOpen(false)}
          durationMS={400}
          style={{
            backgroundColor: "var(--primary)",
            left: "0",
          }}
        >
          {contentControls?.index === 0 && (
            <>
              <img
                src={`/assets/imgs/plans/TYPE-${planID}.svg`}
                alt={unit[0]?.floorplan}
                style={{
                  height: "70%",
                  maxHeight: "70%",
                  position: "absolute",
                  width: "100%",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
              <img
                src={`/assets/icons/compass.svg`}
                alt={unit[0]?.floorplan}
                style={{
                  height: "7%",
                  width: "7%",
                  position: "absolute",
                  bottom: "3rem",
                  left: "3rem",
                }}
              />
            </>
          )}
          {contentControls?.index === 2 && (
            <div
              id="view-modal"
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <SphericalTour
                unit={unit}
                texture={"/assets/imgs/view.webp"}
                initialPosition={[0, 0, 0]}
              />
            </div>
          )}
        </Popup>
      </>
    )
  )
}

export default FloorplanImage

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  .imageOverlayToggleFav {
    position: absolute;
    bottom: 0;
    right: 0;
    margin: 2rem;
    padding: 0.25rem;
    border-radius: 5px;
    aspect-ratio: 1/1;
    background-color: var(--secondary);
    transform: scale(0.75);
    transform-origin: bottom right;
    z-index: 10;
  }
  #view-modal {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    & canvas {
      width: 100% !important;
      height: 100% !important;
    }
  }
`

const FloorplanWrapper = styled.div`
  position: relative;
  width: 100%;
  height: calc(100% - 37rem);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  #floorplan {
    width: 100%;
    height: 100%;

    .plan {
      position: absolute;
      height: 80%;
      max-height: 80%;
      width: auto;
      max-width: 50%;
      object-fit: contain;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .compass {
      position: absolute;
      height: 5rem;
      width: 5rem;
      object-fit: cover;
      bottom: 1rem;
      left: 1rem;
    }
  }

  #view,
  #spec {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    visibility: hidden;
    opacity: 0;
  }
`

const Header = styled.div`
  position: relative;
  width: 100%;
  height: 15rem;
  font-size: 1.5rem;
  line-height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid;
  border-image: linear-gradient(to right, var(--accent), transparent) 1;
  padding: 2rem 2rem 0 2rem;

  & span {
    color: var(--accent);
    font-family: SecondaryFont;
    font-size: 0.8em;
  }
`

const Footer = styled.div`
  position: relative;
  width: 100%;
  height: 22rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 2rem 0 2rem;
  border-top: 1px solid;
  border-image: linear-gradient(to right, var(--accent), transparent) 1;
  background-color: #dfd7cd;

  & svg {
    width: 20%;
    max-height: 80%;
  }
`

const Measures = styled.div`
  width: 65%;
  & div {
    height: 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.8rem;

    & h3:nth-of-type(2) {
      width: 55%;
      opacity: 0.5;
      display: flex;
      justify-content: space-between;

      & span {
        width: 50%;
        display: inline-block;
        margin-left: auto;
        text-align: right;
      }
    }
  }

  & div:nth-last-of-type(3) {
    margin-bottom: 2rem;
  }
  & div:nth-last-of-type(2) {
    border-top: 1px solid;
    border-image: linear-gradient(to right, var(--accent), transparent) 1;
  }
`
